import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { ModalService } from '@cfp/core';
import { BuildingSearchFiltersService } from '@cfp/shared/building/filters';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { PdfService } from '@cfp/shared/ui/pdf';
import { Button, ButtonStyle, Icon } from '@cfp/ui';
import { AlertService, LoadingModal } from '@cfp/ui/feedback';
import { TranslateService } from '@ngx-translate/core';
import { LabelGroupDTO } from 'csr-asset-app';
import { Observable, map } from 'rxjs';

const MAX_BUILDINGS_FOR_PDF = 200;

@Component({
  selector: 'cfp-portfolio-pdf-download-button',
  templateUrl: './portfolio-pdf-download-button.html',
  imports: [Button, Icon],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioPdfDownloadButton {
  public readonly url = input.required<string>();
  public readonly fileName = input.required<string>();
  public readonly dataAnalytics = input<string>();
  public readonly buttonStyle = input<ButtonStyle>('secondary');
  public readonly buttonOutline = input<boolean>(true);

  protected readonly isLoading = signal<boolean>(false);

  private readonly modalService = inject(ModalService);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly pdfService = inject(PdfService);
  private readonly alertService = inject(AlertService);
  private readonly buildingSearchFiltersService = inject(BuildingSearchFiltersService);
  private readonly translateService = inject(TranslateService);

  protected downloadReport(): void {
    let count = this.buildingSearchFiltersService.filteredBuildingsCount();

    if (count === undefined) {
      this.startPdfDownload();
    } else {
      this.checkBuildingsCountBeforeDownload(count);
    }
  }

  private checkBuildingsCountBeforeDownload(count: number): void {
    if (count >= MAX_BUILDINGS_FOR_PDF) {
      const modalBody = this.translateService.instant(`downloadReport.tooManyBuildings.body`, {
        maxBuildings: MAX_BUILDINGS_FOR_PDF
      });
      this.alertService.showAlert('info', 'downloadReport.tooManyBuildings.title', modalBody);
      return;
    }

    this.startPdfDownload();
  }

  private startPdfDownload(): void {
    this.isLoading.set(true);
    const bsModalRef = this.modalService.show(LoadingModal, {
      class: 'modal-sm modal-dialog-centered',
      ignoreBackdropClick: true
    });

    const bsModalContent = bsModalRef.content;
    if (!bsModalContent) {
      return;
    }

    bsModalContent.loading.set(true);
    bsModalContent.loadingText.set('print.report');
    bsModalContent.loadingSubText.set('print.report.subText');
    bsModalContent.onClose.subscribe(() => {
      bsModalRef.hide();
    });

    const labelGroup = this.labelGroupService.getStoredPreferredLabelGroup();

    this.generatePdf(this.url(), this.fileName(), labelGroup ?? undefined).subscribe({
      next: () => {
        this.isLoading.set(false);
        bsModalContent.message.set('print.report.success');
        bsModalContent.success.set(true);
        bsModalContent.loading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        bsModalContent.message.set('print.report.error');
        bsModalContent.success.set(false);
        bsModalContent.loading.set(false);
      }
    });
  }

  private generatePdf(url: string, fileName: string, labelGroup?: LabelGroupDTO): Observable<void> {
    return this.pdfService.getPdfByUrl(url, labelGroup).pipe(
      map((response) => {
        if (window.navigator && (window.navigator as any).msSaveBlob) {
          (window.navigator as any).msSaveBlob(response.body, fileName);
          return;
        }

        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(response.body);
        downloadLink.download = fileName;
        downloadLink.target = '_blank';
        document.body.appendChild(downloadLink);
        downloadLink.click();
      })
    );
  }
}
